/**
 * One store, from the clinic's side.
 *
 * The list could only ever show a name and a status, so the settings that decide
 * what the AI may say about this store had nowhere to be seen or changed - even
 * though the API could already write them and the JMC screens already did.
 *
 * A store setting that is empty inherits the clinic's, and that is shown rather
 * than left to be guessed: "empty" and "inherited" look identical otherwise, and
 * the difference decides what ends up in an article.
 */
import { ThemePicker } from '../components/ThemePicker.tsx';
import { ROLE_LABEL, sessionRole } from '../roles.ts';
import { useState, type ReactNode } from 'react';
import { Link, useLoaderData, useOutletContext } from 'react-router';
import type { Session, StoreDetail } from '../api.ts';
import { patch } from '../api.ts';
import { Card, Empty, Pill, Stat, StatusPill, Table, jaDate, jaNumber } from '../components/ui.tsx';
import { Field, FieldGroup } from '../components/EditPanel.tsx';
import { ModalForm } from '../components/Modal.tsx';
import { InheritedKeywords, KeywordOverride } from '../components/KeywordOverride.tsx';
import {
  StoreProfileFields,
  storeProfileFrom,
  storeProfilePayload,
} from '../components/ProfileFields.tsx';
import { Select } from '../components/Select.tsx';

const FACT_LABEL: Record<string, string> = {
  treatments: '施術・コース・料金',
  personas: 'ペルソナ',
  supervisors: '監修医',
  cta_templates: 'CTAテンプレート',
  strengths: 'クリニックの強み',
};


/** The status, in the words the rest of the product uses for it. */
const STORE_STATUS: Record<string, string> = {
  ACTIVE: '有効',
  SUSPENDED: '停止',
  ARCHIVED: 'アーカイブ',
};

/**
 * One fact about the store: the value, and what it decides.
 *
 * The screen listed a term and a value, which says what is stored and nothing
 * about what it does - and every one of these is material an article may state
 * or a default it is written against. Same block as the clinic settings screen,
 * so the two read as one product.
 */
function Row({ label, value, note }: { label: string; value: ReactNode; note: string }) {
  return (
    <div className="setrow">
      <span className="setrow__label">{label}</span>
      <span className="setrow__value">{value}</span>
      <span className="setrow__note muted small">{note}</span>
    </div>
  );
}

/** An empty override inherits the clinic's value; say so instead of showing nothing. */
function Inherited({ own, inherited }: { own: string | null; inherited: string | null }) {
  if (own) return <>{own}</>;
  if (inherited) {
    return (
      <span className="muted">
        {inherited} <Pill tone="muted">クリニックから継承</Pill>
      </span>
    );
  }
  return <span className="muted">未設定</span>;
}

export default function StoreDetailPage() {
  const { store, facts, members } = useLoaderData<StoreDetail>();
  const session = useOutletContext<Session>();
  const isClinicAdmin = !!session.memberships.find((m) => m.clinicId === session.activeClinicId)
    ?.clinicAdmin;
  /*
   * A clinic admin manages every store; a store admin the one they run - its
   * details and its people, not its status (see PATCH /stores/:id).
   */
  const canManage =
    isClinicAdmin || sessionRole(session).manageStores.some((own) => own.id === store.id);

  const [editing, setEditing] = useState(false);
  const [editingSettings, setEditingSettings] = useState(false);
  const [form, setForm] = useState({ name: store.name, status: store.status });
  // Where the branch is, kept beside its name because they are one record.
  const [profile, setProfile] = useState(() => storeProfileFrom(store));
  const [settings, setSettings] = useState({
    siteUrl: store.site_url ?? '',
    defaultTone: store.default_tone ?? '',
    wordpressCategory: store.wordpress_category ?? '',
    useClinicFacts: store.use_clinic_facts,
  });
  // null is inherit, [] is "none of its own" - see KeywordOverride for why the
   // difference has to survive all the way to the request body.
  const [keywords, setKeywords] = useState<string[] | null>(store.default_keywords);

  // Name and status live on `stores`; the article defaults live on
  // `store_settings`. Two tables, two endpoints, so two panels rather than one
  // form that half-succeeds.
  const saveStore = () =>
    patch(`/stores/${store.id}`, {
      name: form.name,
      // Opening or closing a branch is the clinic admin's; the server refuses it otherwise.
      ...(isClinicAdmin ? { status: form.status } : {}),
      ...storeProfilePayload(profile),
    });

  // Empty means "inherit from the clinic", which is null in the database - not
  // an empty string. The two render differently above, so they must be sent
  // differently here.
  const saveSettings = () =>
    patch(`/stores/${store.id}/settings`, {
      siteUrl: settings.siteUrl.trim() || null,
      defaultTone: settings.defaultTone.trim() || null,
      wordpressCategory: settings.wordpressCategory.trim() || null,
      useClinicFacts: settings.useClinicFacts,
      // null hands the store back to the clinic's list; an array - empty
      // included - is this store's own.
      defaultKeywords: keywords,
    });

  const totalFacts = facts.reduce((sum, fact) => sum + Number(fact.total), 0);
  /*
   * Approved separately, because that is the number that decides anything: an
   * article may only state approved material, and a store with twelve rows of
   * which three are approved can write from three. The header said 12 while the
   * store list said 3, which reads as one of them being wrong.
   */
  const approvedFacts = facts.reduce((sum, fact) => sum + Number(fact.approved), 0);

  return (
    <>
      <Card
        title={store.name}
        caption={`${store.clinic_name}の店舗`}
        actions={
          <>
            <Link className="btn btn--sm btn--ghost" to="/stores">
              一覧に戻る
            </Link>
            {canManage && !editing && (
              <button className="btn btn--sm" type="button" onClick={() => setEditing(true)}>
                編集
              </button>
            )}
          </>
        }
      >
        <div className="grid grid--4">
          <Stat label="ステータス" value={STORE_STATUS[store.status] ?? store.status} small />
          <Stat label="担当ユーザー" value={`${jaNumber(members.length)}人`} small />
          <Stat
            label="承認済みの情報"
            value={`${jaNumber(approvedFacts)} / ${jaNumber(totalFacts)}`}
            small
          />
          <Stat label="登録日" value={jaDate(store.created_at)} small />
        </div>

        <ModalForm
          open={editing}
          onClose={() => setEditing(false)}
          onSubmit={saveStore}
          title="店舗情報を編集"
          caption="住所・診療時間・予約先は、この店舗の記事にそのまま引用されます。"
          wide
        >
          <FieldGroup title="この店舗について" note="一覧と記事の両方に出ます" />
          <Field label="店舗名" htmlFor="st-name" required>
            <input
              id="st-name"
              value={form.name}
              maxLength={200}
              required
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Field>
          {isClinicAdmin && (
          <Field
            label="ステータス"
            htmlFor="st-status"
            hint="アーカイブした店舗では新しい記事を作成できません。既存の記事は残ります。"
          >
            <Select
              id="st-status"
              value={form.status}
              onChange={(status) => setForm({ ...form, status })}
              options={[
                { value: 'ACTIVE', label: '有効' },
                { value: 'SUSPENDED', label: '停止' },
                { value: 'ARCHIVED', label: 'アーカイブ' },
              ]}
            />
          </Field>
          )}
          <StoreProfileFields idPrefix="st" value={profile} onChange={setProfile} />
        </ModalForm>

        {/*
          Shown rather than hidden behind the edit button: "where is this branch
          and when is it open" is what somebody opens a store screen to check,
          and it is also exactly what the generator may state about it.
        */}
        <div className="setgrid">
          <Row
            label="住所"
            value={
              [store.postal_code && `〒${store.postal_code}`, store.address]
                .filter(Boolean)
                .join(' ') || <span className="muted">未登録</span>
            }
            note="記事の運営者情報とアクセス案内に使われます"
          />
          <Row
            label="アクセス"
            value={store.access_note || <span className="muted">未登録</span>}
            note="最寄り駅と徒歩分数。記事にそのまま引用されます"
          />
          <Row
            label="診療時間"
            value={
              <>
                {store.hours_note || <span className="muted">未登録</span>}
                {store.closed_note && (
                  <span className="muted small" style={{ display: 'block' }}>
                    休診：{store.closed_note}
                  </span>
                )}
              </>
            }
            note="曜日ごとに違う場合はその旨まで書けます"
          />
          <Row
            label="電話・予約"
            value={
              <>
                {store.phone || '—'}
                {store.reservation_url && (
                  <>
                    {' / '}
                    <a href={store.reservation_url} target="_blank" rel="noreferrer">
                      予約ページ
                    </a>
                  </>
                )}
              </>
            }
            note="CTAの導線に使われます"
          />
        </div>
      </Card>

      {/*
        A branch that trades under its own look. Store admins and clinic admins
        decide it; staff see it. Empty inherits the clinic's colour.
      */}
      <ThemePicker
        endpoint={`/stores/${store.id}/theme`}
        value={store.brand_color ?? null}
        inherited={{ label: 'クリニックの色', hex: store.clinic_brand_color ?? null }}
        canEdit={canManage}
      />

      <Card
        title="記事の初期設定"
        caption="空欄の項目はクリニックの設定を引き継ぎます。ここで上書きすると、この店舗の記事にだけ適用されます。"
        actions={
          !editingSettings && (
            <button className="btn btn--sm" type="button" onClick={() => setEditingSettings(true)}>
              編集
            </button>
          )
        }
      >
        <div className="setgrid">
          <Row
            label="サイトURL"
            value={<Inherited own={store.site_url} inherited={store.clinic_site_url} />}
            note="この店舗の記事の掲載先です"
          />
          <Row
            label="文体・トーン"
            value={<Inherited own={store.default_tone} inherited={store.clinic_default_tone} />}
            note="空欄ならクリニックの設定で書かれます"
          />
          <Row
            label="対策キーワード（既定）"
            value={
              <InheritedKeywords
                own={store.default_keywords}
                inherited={store.clinic_default_keywords}
              />
            }
            note="記事ごとのキーワードと合わせて渡されます"
          />
          <Row
            label="WordPressカテゴリ"
            value={
              <Inherited
                own={store.wordpress_category}
                inherited={store.clinic_wordpress_category}
              />
            }
            note="書き出したときの既定のカテゴリ名です"
          />
          <Row
            label="クリニック共通の情報"
            value={
              store.use_clinic_facts ? <Pill tone="ok">使う</Pill> : <Pill tone="warn">使わない</Pill>
            }
            note={
              store.use_clinic_facts
                ? '共通の施術・監修医・強みも、この店舗の記事に使われます'
                : 'この店舗に登録された情報だけで記事が作られます'
            }
          />
        </div>

        <ModalForm
          open={editingSettings}
          onClose={() => setEditingSettings(false)}
          onSubmit={saveSettings}
          title="記事の初期設定を編集"
          caption="空欄にすると、その項目はクリニックの設定を引き継ぎます。"
          wide
        >
          <Field
            label="サイトURL"
            htmlFor="st-url"
            hint="https から始まるこの店舗のページ。空欄にするとクリニックの設定に戻ります。"
          >
            <input
              id="st-url"
              type="url"
              value={settings.siteUrl}
              placeholder={store.clinic_site_url ?? 'https://'}
              onChange={(e) => setSettings({ ...settings, siteUrl: e.target.value })}
            />
          </Field>
          <Field label="トーン" htmlFor="st-tone" hint="例：やさしい、専門的">
            <input
              id="st-tone"
              value={settings.defaultTone}
              maxLength={40}
              placeholder={store.clinic_default_tone ?? ''}
              onChange={(e) => setSettings({ ...settings, defaultTone: e.target.value })}
            />
          </Field>
          <Field
            label="対策キーワード（既定）"
            htmlFor="st-keywords"
            span="full"
            hint="この店舗の記事に、記事ごとのキーワードと合わせて渡されます。"
          >
            <KeywordOverride
              id="st-keywords"
              value={keywords}
              inherited={store.clinic_default_keywords}
              onChange={setKeywords}
            />
          </Field>
          <Field label="WordPressカテゴリ" htmlFor="st-cat">
            <input
              id="st-cat"
              value={settings.wordpressCategory}
              maxLength={120}
              placeholder={store.clinic_wordpress_category ?? ''}
              onChange={(e) => setSettings({ ...settings, wordpressCategory: e.target.value })}
            />
          </Field>
          <Field
            label="クリニック共通の情報を使う"
            htmlFor="st-clinicfacts"
            span="full"
            hint="オフにすると、この店舗の記事はこの店舗に登録された情報だけで作られます。独自の価格やメニューで運営している店舗向けの設定です。"
          >
            {/*
              A checkbox, not a Select: it is one binary thing, and a two-option
              dropdown for a yes/no is a click nobody needs. The label above it is
              phrased as the affirmative so a ticked box always means "yes".
            */}
            <label className="checkline">
              <input
                id="st-clinicfacts"
                type="checkbox"
                checked={settings.useClinicFacts}
                onChange={(e) => setSettings({ ...settings, useClinicFacts: e.target.checked })}
              />
              <span>共通の施術メニュー・監修医・強みなどを、この店舗の記事にも使う</span>
            </label>
          </Field>
        </ModalForm>
      </Card>

      <div className="grid grid--2">
        <Card title="この店舗を担当するユーザー">
          {members.length === 0 ? (
            <Empty>担当ユーザーがいません。ユーザー管理から追加できます。</Empty>
          ) : (
            <Table
              head={
                <>
                  <th scope="col">氏名</th>
                  <th scope="col">権限</th>
                  <th scope="col">状態</th>
                </>
              }
            >
              {members.map((member) => (
                <tr key={member.user_id} className={canManage ? 'row-link' : undefined}>
                  <td>
                    {canManage ? (
                      <Link className="row-link__target" to={`/members/${member.user_id}`}>
                        {member.displayName || (member.email ?? '—')}
                      </Link>
                    ) : (
                      (member.displayName || member.email) ?? '—'
                    )}
                  </td>
                  <td>{ROLE_LABEL[member.role] ?? member.role}</td>
                  <td>
                    <StatusPill status={member.status} />
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Card>

        <Card title="この店舗が使える情報">
          <Table
            head={
              <>
                <th scope="col">種類</th>
                <th scope="col">承認済み</th>
                <th scope="col">合計</th>
              </>
            }
          >
            {facts.map((fact) => (
              <tr key={fact.kind}>
                <td>{FACT_LABEL[fact.kind] ?? fact.kind}</td>
                <td>{jaNumber(fact.approved)}</td>
                <td className="muted">{jaNumber(fact.total)}</td>
              </tr>
            ))}
          </Table>
          <p className="muted small">
            記事に書けるのは承認済みの情報だけです。承認前の情報はAIに渡りません。
          </p>
        </Card>
      </div>
    </>
  );
}
